<template>
  <div class="po-invoice-listing">
    <!-- Top Summary Metrics Cards Covering All Statuses -->
    <v-row class="mb-2">
      <!-- 1. Total Invoices -->
      <v-col cols="12" sm="6" md="4">
        <v-card
          flat
          class="custom-grey-border pa-3 rounded-lg d-flex align-center h-100"
        >
          <v-avatar color="cyan lighten-5" size="44" class="mr-3">
            <v-icon color="cyan darken-2">mdi-receipt</v-icon>
          </v-avatar>
          <div>
            <div class="caption grey--text font-weight-600 text-uppercase">
              Total Invoices
            </div>
            <div class="title font-weight-700 cyan--text text--darken-2">
              {{ summaryMetrics.totalCount }} Invoices
            </div>
            <div class="font-size-11 grey--text text--darken-1 font-weight-600">
              Total: {{ formatMoney(summaryMetrics.totalAmount) }}
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- 2. Paid Invoices -->
      <v-col cols="12" sm="6" md="4">
        <v-card
          flat
          class="custom-grey-border pa-3 rounded-lg d-flex align-center h-100"
        >
          <v-avatar color="green lighten-5" size="44" class="mr-3">
            <v-icon color="green darken-2">mdi-check-circle-outline</v-icon>
          </v-avatar>
          <div>
            <div class="caption grey--text font-weight-600 text-uppercase">
              Paid Invoices
            </div>
            <div class="title font-weight-700 green--text text--darken-2">
              {{ summaryMetrics.paidCount }} Paid
            </div>
            <div class="font-size-11 grey--text text--darken-1 font-weight-600">
              Settled: {{ formatMoney(summaryMetrics.paidAmount) }}
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- 3. Partially Paid Invoices -->
      <!-- <v-col cols="12" sm="6" md="3">
        <v-card flat class="custom-grey-border pa-3 rounded-lg d-flex align-center h-100">
          <v-avatar color="orange lighten-5" size="44" class="mr-3">
            <v-icon color="orange darken-2">mdi-progress-clock</v-icon>
          </v-avatar>
          <div>
            <div class="caption grey--text font-weight-600 text-uppercase">
              Partially Paid
            </div>
            <div class="title font-weight-700 orange--text text--darken-2">
              {{ summaryMetrics.partiallyPaidCount }} Partial
            </div>
            <div class="font-size-11 grey--text text--darken-1 font-weight-600">
              Paid: {{ formatMoney(summaryMetrics.partiallyPaidAmount) }}
            </div>
          </div>
        </v-card>
      </v-col> -->

      <!-- 4. Unpaid / Pending Invoices -->
      <v-col cols="12" sm="6" md="4">
        <v-card
          flat
          class="custom-grey-border pa-3 rounded-lg d-flex align-center h-100"
        >
          <v-avatar color="red lighten-5" size="44" class="mr-3">
            <v-icon color="red darken-2">mdi-alert-circle-outline</v-icon>
          </v-avatar>
          <div>
            <div class="caption grey--text font-weight-600 text-uppercase">
              Unpaid / Pending
            </div>
            <div class="title font-weight-700 red--text text--darken-2">
              {{ summaryMetrics.unpaidCount }} Unpaid
            </div>
            <div class="font-size-11 grey--text text--darken-1 font-weight-600">
              Balance: {{ formatMoney(summaryMetrics.balanceAmount) }}
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filter Bar Matching Internal-Invoice.vue -->
    <v-layout class="my-2 align-center flex-wrap">
      <v-flex md3 sm5 xs12 class="pr-2">
        <DateRangePicker
          placeholder="Date Range"
          v-model="dates"
          v-on:update:model-value="onFilterChange()"
          v-on:click:clear="clearFilter('dates')"
          class="mr-2"
        ></DateRangePicker>
      </v-flex>
      <v-flex md4 sm5 xs12>
        <div class="d-flex">
          <v-text-field
            v-model.trim="search"
            v-on:click:clear="clearFilter('search')"
            clearable
            dense
            filled
            flat
           
            placeholder="Search by title or invoice number..."
            hide-details
            solo
            :loading="invoiceLoading"
            :disabled="invoiceLoading"
            color="cyan"
            @keydown.enter="onFilterChange()"
            @keydown.tab="onFilterChange()"
          ></v-text-field>
          <v-btn
            class="mx-2 custom-bold-button white--text"
            color="cyan"
            tile
            depressed
            :disabled="invoiceLoading"
            v-on:click="onFilterChange()"
          >
            <v-icon>mdi-magnify</v-icon>
          </v-btn>
          <v-btn
            class="mx-2 custom-bold-button white--text"
            depressed
            :disabled="invoiceLoading"
            v-on:click="onFilterChange()"
            color="cyan white--text"
          >
            <v-icon>mdi-reload</v-icon>
          </v-btn>
        </div>
      </v-flex>
      <v-spacer></v-spacer>
      <v-flex md3 sm12 xs12 class="text-right my-1" v-if="isApproved">
        <v-btn
          color="cyan"
          class="custom-bold-button white--text"
          depressed
          tile
          v-on:click="openCreateDialog"
        >
          <v-icon small left>mdi-plus</v-icon>
          Create Invoice
        </v-btn>
      </v-flex>
    </v-layout>

    <!-- Listing Table Matching Internal-Invoice.vue -->
    <ListingTable :columnCount="isApproved ? 7 : 6" :rowData="rowData">
      <template v-slot:thead>
        <thead>
          <tr>
            <th class="simple-table-th" style="width: 140px">Invoice #</th>
            <th
              class="simple-table-th"
              style="min-width: 220px; max-width: 320px"
            >
              Invoice Title
            </th>
            <th class="simple-table-th" style="width: 130px">Dates</th>
            <th class="simple-table-th" style="width: 160px">Amount</th>
            <th class="simple-table-th" style="width: 120px">Status</th>
            <th class="simple-table-th" style="width: 170px">Created At</th>
            <th
              class="simple-table-th text-center"
              style="width: 90px"
              v-if="isApproved"
            >
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-if="rowData && rowData.length">
            <tr
              v-for="(row, index) in rowData"
              :key="row.id || index"
              class="alternate-listing-row"
            >
              <!-- 1. Invoice # -->
              <td class="simple-table-td">
                <Barcode :barcode="row.invoice_number"></Barcode>
              </td>

              <!-- 2. Invoice Title with Truncated Remark & Hover Tooltip -->
              <td class="simple-table-td" style="max-width: 320px">
                <p
                  class="m-0 custom-nowrap-ellipsis font-weight-600 color-custom-blue"
                  :title="row.invoice_title"
                >
                  {{ row.invoice_title }}
                </p>
                <template v-if="row.remark && row.remark.trim()">
                  <v-tooltip
                    top
                    max-width="380"
                    content-class="custom-top-tooltip"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <p
                        v-bind="attrs"
                        v-on="on"
                        class="m-0 font-size-11 grey--text text--darken-1 custom-nowrap-ellipsis cursor-pointer d-flex align-center mt-1"
                        style="max-width: 280px"
                      >
                        <v-icon x-small color="grey darken-1" class="mr-1"
                          >mdi-comment-text-outline</v-icon
                        >
                        <span class="text-truncate">{{
                          truncateText(row.remark, 42)
                        }}</span>
                      </p>
                    </template>
                    <span>{{ row.remark }}</span>
                  </v-tooltip>
                </template>
              </td>

              <!-- 3. Dates -->
              <td class="simple-table-td">
                <p class="m-0 custom-nowrap-ellipsis">
                  <b>Issued: </b>
                  <template v-if="row.invoice_date">
                    {{ formatDate(row.invoice_date) }}
                  </template>
                  <template v-else>
                    <em class="text--secondary">No Date</em>
                  </template>
                </p>
              </td>

              <!-- 4. Amount (Total / Paid / Balance) -->
              <td class="simple-table-td">
                <div class="invoice-listing-amount">
                  <p class="m-0 custom-nowrap-ellipsis">
                    <b>Total: </b>
                    <label class="m-0 color-custom-blue font-weight-600">
                      {{ formatMoney(row.total_amount) }}
                    </label>
                  </p>
                  <p class="m-0 custom-nowrap-ellipsis">
                    <b>Paid: </b>
                    <label class="m-0 green--text font-weight-600">
                      {{ formatMoney(row.paid_amount) }}
                    </label>
                  </p>
                  <p class="m-0 custom-nowrap-ellipsis">
                    <b>Balance: </b>
                    <label
                      class="m-0 font-weight-600"
                      :class="{
                        'orange--text': Number(row.balance_amount) > 0,
                        'green--text': Number(row.balance_amount) <= 0,
                      }"
                    >
                      {{ formatMoney(row.balance_amount) }}
                    </label>
                  </p>
                </div>
              </td>

              <!-- 5. Status -->
              <td class="simple-table-td">
                <v-chip
                  small
                  label
                  class="font-weight-600 custom-status text-uppercase font-10"
                  :color="getInvoiceStatusColor(row.status)"
                  text-color="white"
                >
                  {{ row.status }}
                </v-chip>
              </td>

              <!-- 6. Activity (TableActivity using added_by and added_at) -->
              <td class="simple-table-td">
                <TableActivity v-if="!lodash.isEmpty(row.added_by)" :data="row">
                  <template v-slot:first_letters>
                    {{ getFirstLetters(row.added_by?.display_name) }}
                  </template>
                  <template v-slot:display_name>
                    {{ row.added_by.display_name }}
                  </template>
                  <template v-slot:date_time>
                    <template v-if="getDateCountAll(row.added_at)">{{
                      row.created_at
                    }}</template>
                    <template v-else>{{
                      formatDateTime(row.added_at)
                    }}</template>
                  </template>
                  <template v-slot:format_date_time>
                    {{ formatDateTime(row.added_at) }}
                  </template>
                </TableActivity>
                <div
                  v-else-if="row.added_at"
                  class="custom-table-content max-width-150p d-flex align-center"
                >
                  <v-avatar size="30" color="grey darken-1" class="mr-2">
                    <span class="text-uppercase white--text font-size-14 fw-600"
                      >AD</span
                    >
                  </v-avatar>
                  <div>
                    <p
                      class="m-0 custom-nowrap-ellipsis font-weight-medium py-0"
                    >
                      Admin
                    </p>
                    <p
                      class="m-0 py-0 custom-nowrap-ellipsis font-size-11 grey--text"
                    >
                      {{ formatDateTime(row.added_at) }}
                    </p>
                  </div>
                </div>
                <template v-else>--</template>
              </td>

              <!-- 7. Action (shown only if isApproved) -->
              <td class="simple-table-td text-center" v-if="isApproved">
                <!-- View Details -->
                <v-tooltip top content-class="custom-top-tooltip">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="cyan darken-2"
                      v-bind="attrs"
                      v-on="on"
                      @click="viewInvoice(row)"
                    >
                      <v-icon small>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>View Details</span>
                </v-tooltip>

                <!-- Edit Invoice (Disabled when Paid) -->
                <v-tooltip top content-class="custom-top-tooltip">
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <v-btn
                        icon
                        small
                        color="cyan darken-2"
                        :disabled="row.status === 'Paid'"
                        @click="openEditDialog(row)"
                      >
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                    </span>
                  </template>
                  <span>{{
                    row.status === "Paid"
                      ? "Paid invoices cannot be edited"
                      : "Edit Invoice"
                  }}</span>
                </v-tooltip>
              </td>
            </tr>
          </template>
          <template v-else>
            <tr>
              <td :colspan="isApproved ? 7 : 6">
                <p
                  class="m-0 row-not-found text-center font-weight-500 font-size-16"
                >
                  <img
                    :src="$assetURL('media/error/empty.png')"
                    class="row-not-found-image"
                  />
                  Uhh... There are no invoice at the moment.
                </p>
              </td>
            </tr>
          </template>
        </tbody>
      </template>
    </ListingTable>

    <!-- Listing Footer Matching Internal-Invoice.vue -->
    <ListingFooter
      :dataLoading="invoiceLoading"
      :showingFrom="showingFrom"
      :showingTo="showingTo"
      :totalRows="totalRows"
      :currentPage="currentPage"
      :totalPages="totalPages"
      :removePage="true"
    ></ListingFooter>

    <!-- Create / Edit Invoice Dialog -->
    <Dialog :commonDialog="invoiceDialog" :dialogWidth="768">
      <template v-slot:title>
        <div class="d-flex align-center width-100">
          <span class="font-weight-700 color-custom-blue">
            {{ isEdit ? "Edit PO Invoice" : "Create PO Invoice" }}
            <span
              v-if="detail && detail.barcode"
              class="grey--text text--darken-1 font-weight-500"
            >
              - {{ detail.barcode }}
            </span>
          </span>
          <v-spacer></v-spacer>
          <v-btn icon aria-label="Close dialog" @click="closeInvoiceDialog">
            <v-icon color="grey darken-1">mdi-close</v-icon>
          </v-btn>
        </div>
      </template>

      <template v-slot:body>
        <v-form
          ref="poInvoiceForm"
          v-model="formValid"
          lazy-validation
          class="pt-2"
        >
          <v-container fluid class="py-0 px-2">
            <v-row>
              <!-- Invoice Title -->
              <v-col cols="12" class="py-1">
                <label
                  class="font-size-16 font-weight-500 required"
                  for="invoice_title"
                >
                  Invoice Title
                </label>
                <TextField
                  v-model.trim="form.invoice_title"
                  dense
                  filled
                  solo
                  flat
                  color="cyan"
                  placeholder="Invoice Title"
                  counter="100"
                  :maxlength="100"
                  :rules="[validateRules.required(form.invoice_title, 'Title')]"
                />
              </v-col>

              <!-- Invoice Date -->
              <v-col cols="12" md="6" class="py-1">
                <label
                  class="font-size-16 font-weight-500 required"
                  for="invoice_date"
                >
                  Invoice Date
                </label>
                <DatePicker
                  :placeholder="'Invoice Date'"
                  solo
                  v-model="form.invoice_date"
                  :defaultDate="form.invoice_date"
                  :mandatory="true"
                  v-on:update:date-picker="form.invoice_date = $event"
                ></DatePicker>
              </v-col>

              <!-- Invoice Number -->
              <v-col cols="12" md="6" class="py-1">
                <label
                  class="font-size-16 font-weight-500 required"
                  for="invoice_number"
                >
                  Invoice Number
                </label>
                  <TextField
                   id="invoice_number"
                  v-model.trim="form.invoice_number"
                  dense
                  filled
                  solo
                  flat
                  color="cyan"
                  placeholder="Invoice Number"
                  counter="100"
                  :maxlength="100"
                  :rules="[validateRules.required(form.invoice_number, 'Number')]"
                />
              </v-col>

              <!-- Total Amount -->
              <v-col cols="12" md="6" class="py-1">
                <label
                  class="font-size-16 font-weight-500 required"
                  for="total_amount"
                >
                  Total Amount
                </label>
                <v-text-field
                  id="total_amount"
                  v-model.number="form.total_amount"
                  dense
                  filled
                  solo
                  flat
                  type="number"
                  min="0"
                  step="0.01"
                  color="cyan"
                  placeholder="Total Amount"
                  :rules="totalAmountRules"
                ></v-text-field>
              </v-col>

              <!-- Paid Amount -->
              <v-col cols="12" md="6" class="py-1">
                <label
                  class="font-size-16 font-weight-500 required"
                  for="paid_amount"
                >
                  Paid Amount
                </label>
                <v-text-field
                  id="paid_amount"
                  v-model.number="form.paid_amount"
                  dense
                  filled
                  solo
                  flat
                  type="number"
                  min="0"
                  step="0.01"
                  color="cyan"
                  placeholder="Paid Amount"
                  :rules="paidAmountRules"
                ></v-text-field>
              </v-col>

              <!-- Remark with TextAreaField component (250 char limit) -->
              <v-col cols="12" class="py-1">
                <label
                  class="font-size-16 font-weight-500"
                  for="invoice_remark"
                >
                  Remark
                </label>
                <TextAreaField
                  id="invoice_remark"
                  v-model="form.remark"
                  auto-grow
                  dense
                  filled
                  solo
                  flat
                  color="cyan"
                  placeholder="Enter remark (max 250 characters)..."
                  :rows="3"
                  row-height="25"
                  counter="250"
                  :disabled="formLoading"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </template>

      <template v-slot:action>
        <v-btn
          depressed
          class="custom-grey-border custom-bold-button"
          :disabled="formLoading"
          @click="closeInvoiceDialog"
        >
          Cancel
        </v-btn>
        <v-btn
          depressed
          color="cyan"
          class="custom-bold-button white--text ml-2"
          :loading="formLoading"
          :disabled="formLoading || !formValid"
          @click="submitInvoice"
        >
          {{ isEdit ? "Update Invoice" : "Save Invoice" }}
        </v-btn>
      </template>
    </Dialog>

    <!-- View Invoice Details Dialog - Enhanced Spacious Layout (850px) -->
    <Dialog :commonDialog="viewDialog" :dialogWidth="850">
      <template v-slot:title>
        <div class="d-flex align-center width-100">
          <v-icon color="cyan darken-2" class="mr-2"
            >mdi-file-document-outline</v-icon
          >
          <span class="font-weight-700 color-custom-blue">
            Invoice Details
            <span
              v-if="selectedInvoice"
              class="grey--text text--darken-1 font-weight-500 font-size-14 ml-1"
            >
              - {{ selectedInvoice.invoice_number }}
            </span>
          </span>
          <v-spacer></v-spacer>
          <v-chip
            v-if="selectedInvoice"
            label
            class="font-weight-700 text-uppercase font-size-11 mr-3"
            :color="getInvoiceStatusColor(selectedInvoice.status)"
            text-color="white"
          >
            <v-icon x-small left>
              {{
                selectedInvoice.status === "Paid"
                  ? "mdi-check-circle"
                  : selectedInvoice.status === "Partially Paid"
                  ? "mdi-clock-outline"
                  : "mdi-alert-circle"
              }}
            </v-icon>
            {{ selectedInvoice.status }}
          </v-chip>
          <v-btn icon aria-label="Close dialog" @click="viewDialog = false">
            <v-icon color="grey darken-1">mdi-close</v-icon>
          </v-btn>
        </div>
      </template>

      <template v-slot:body>
        <v-container fluid class="py-2 px-3" v-if="selectedInvoice">
          <!-- Top Info Banner -->
          <div
            class="pa-3 mb-3 rounded-lg"
            style="background: #f0f7fa; border: 1px solid #d2e8f1"
          >
            <v-row align="center" no-gutters>
              <v-col cols="12" sm="8">
                <div
                  class="headline font-weight-700 color-custom-blue text-capitalize"
                >
                  {{ selectedInvoice.invoice_title }}
                </div>
                <div
                  class="d-flex align-center mt-1 flex-wrap font-size-12 grey--text text--darken-2"
                >
                  <span class="mr-3 d-flex align-center">
                    <v-icon x-small color="cyan darken-2" class="mr-1"
                      >mdi-barcode</v-icon
                    >
                    <b>Invoice #:</b>&nbsp;{{ selectedInvoice.invoice_number }}
                  </span>
                  <span
                    class="mr-3 d-flex align-center"
                    v-if="detail && (detail.barcode || detail.id)"
                  >
                    <v-icon x-small color="cyan darken-2" class="mr-1"
                      >mdi-file-table-outline</v-icon
                    >
                    <b>PO #:</b>&nbsp;{{ detail.barcode || "PO-" + detail.id }}
                  </span>
                  <span class="d-flex align-center">
                    <v-icon x-small color="cyan darken-2" class="mr-1"
                      >mdi-calendar-range</v-icon
                    >
                    <b>Issued:</b>&nbsp;{{
                      formatDate(selectedInvoice.invoice_date)
                    }}
                  </span>
                </div>
              </v-col>
              <v-col cols="12" sm="4" class="text-sm-right mt-2 mt-sm-0">
                <Barcode :barcode="selectedInvoice.invoice_number"></Barcode>
              </v-col>
            </v-row>
          </div>

          <!-- Financial & Payment Summary Card with Progress Bar -->
          <v-card
            flat
            class="custom-grey-border rounded-lg pa-4 mb-3 grey lighten-5"
          >
            <div
              class="caption text-uppercase font-weight-700 grey--text text--darken-2 mb-2 d-flex align-center"
            >
              <v-icon small color="cyan darken-2" class="mr-1"
                >mdi-cash-multiple</v-icon
              >
              Payment & Balance Summary
            </div>
            <v-row align="center" class="mb-2">
              <v-col cols="12" sm="4" class="text-center text-sm-left">
                <div
                  class="caption text-uppercase font-weight-600 grey--text text--darken-1"
                >
                  Total Amount
                </div>
                <div class="headline font-weight-700 color-custom-blue">
                  {{ formatMoney(selectedInvoice.total_amount) }}
                </div>
              </v-col>
              <v-col cols="12" sm="4" class="text-center text-sm-left">
                <div
                  class="caption text-uppercase font-weight-600 grey--text text--darken-1"
                >
                  Paid Amount
                </div>
                <div
                  class="headline font-weight-700 green--text text--darken-2"
                >
                  {{ formatMoney(selectedInvoice.paid_amount) }}
                </div>
              </v-col>
              <v-col cols="12" sm="4" class="text-center text-sm-left">
                <div
                  class="caption text-uppercase font-weight-600 grey--text text--darken-1"
                >
                  Balance Amount
                </div>
                <div
                  class="headline font-weight-700"
                  :class="{
                    'orange--text text--darken-2':
                      Number(selectedInvoice.balance_amount) > 0,
                    'green--text text--darken-2':
                      Number(selectedInvoice.balance_amount) <= 0,
                  }"
                >
                  {{ formatMoney(selectedInvoice.balance_amount) }}
                </div>
              </v-col>
            </v-row>
            <!-- Payment Progress Bar -->
            <div class="pt-2">
              <div
                class="d-flex justify-space-between caption font-weight-600 grey--text text--darken-2 mb-1"
              >
                <span>Payment Progress</span>
                <span
                  :class="
                    selectedInvoice.status === 'Paid'
                      ? 'green--text font-weight-700'
                      : 'blue--text'
                  "
                >
                  {{ calculatePaymentPercentage(selectedInvoice) }}% Paid
                </span>
              </div>
              <v-progress-linear
                :value="calculatePaymentPercentage(selectedInvoice)"
                height="8"
                rounded
                :color="
                  selectedInvoice.status === 'Paid'
                    ? 'green'
                    : selectedInvoice.status === 'Partially Paid'
                    ? 'orange darken-1'
                    : 'grey lighten-1'
                "
                background-color="grey lighten-3"
              ></v-progress-linear>
            </div>
          </v-card>

          <!-- Two-Column Information Layout -->
          <v-row class="mb-3">
            <!-- Left: Invoice Specifications -->
            <v-col cols="12" md="6">
              <v-card flat class="custom-grey-border rounded-lg pa-3 h-100">
                <div
                  class="caption text-uppercase font-weight-700 grey--text text--darken-2 mb-2 d-flex align-center"
                >
                  <v-icon small color="cyan darken-2" class="mr-1"
                    >mdi-information-outline</v-icon
                  >
                  Invoice Specifications
                </div>
                <table class="width-100 font-size-13">
                  <tbody>
                    <tr>
                      <td
                        class="font-weight-600 py-2 grey--text text--darken-1"
                        style="width: 140px"
                      >
                        Invoice Title:
                      </td>
                      <td class="font-weight-600 color-custom-blue py-2">
                        {{ selectedInvoice.invoice_title }}
                      </td>
                    </tr>
                    <tr class="custom-border-top">
                      <td
                        class="font-weight-600 py-2 grey--text text--darken-1"
                      >
                        Invoice Number:
                      </td>
                      <td class="font-weight-600 py-2">
                        {{ selectedInvoice.invoice_number }}
                      </td>
                    </tr>
                    <tr class="custom-border-top">
                      <td
                        class="font-weight-600 py-2 grey--text text--darken-1"
                      >
                        Issue Date:
                      </td>
                      <td class="py-2">
                        {{ formatDate(selectedInvoice.invoice_date) }}
                      </td>
                    </tr>
                    <tr
                      class="custom-border-top"
                      v-if="detail && detail.barcode"
                    >
                      <td
                        class="font-weight-600 py-2 grey--text text--darken-1"
                      >
                        Purchase Order:
                      </td>
                      <td class="py-2 font-weight-600">{{ detail.barcode }}</td>
                    </tr>
                    <tr class="custom-border-top">
                      <td
                        class="font-weight-600 py-2 grey--text text--darken-1"
                      >
                        Payment Status:
                      </td>
                      <td
                        class="py-2 font-weight-700"
                        :class="
                          getInvoiceStatusTextColor(selectedInvoice.status)
                        "
                      >
                        {{ selectedInvoice.status }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </v-card>
            </v-col>

            <!-- Right: Activity & Audit Trail -->
            <v-col cols="12" md="6">
              <v-card flat class="custom-grey-border rounded-lg pa-3 h-100">
                <div
                  class="caption text-uppercase font-weight-700 grey--text text--darken-2 mb-2 d-flex align-center"
                >
                  <v-icon small color="cyan darken-2" class="mr-1"
                    >mdi-history</v-icon
                  >
                  Activity & Audit Trail
                </div>
                <div class="py-1">
                  <div
                    class="font-weight-600 font-size-12 grey--text text--darken-2 mb-1"
                  >
                    Created / Added By:
                  </div>
                  <TableActivity
                    :data="selectedInvoice"
                    v-if="!lodash.isEmpty(selectedInvoice.added_by)"
                  >
                    <template v-slot:first_letters>
                      {{
                        getFirstLetters(selectedInvoice.added_by?.display_name)
                      }}
                    </template>
                    <template v-slot:display_name>
                      {{ selectedInvoice.added_by.display_name }}
                    </template>
                    <template v-slot:date_time>
                      <template
                        v-if="getDateCountAll(selectedInvoice.added_at)"
                        >{{ selectedInvoice.created_at }}</template
                      >
                      <template v-else>{{
                        formatDateTime(selectedInvoice.added_at)
                      }}</template>
                    </template>
                    <template v-slot:format_date_time>
                      {{ formatDateTime(selectedInvoice.added_at) }}
                    </template>
                  </TableActivity>
                  <div v-else class="font-size-12 grey--text">
                    {{
                      formatDateTime(selectedInvoice.added_at) || "Not recorded"
                    }}
                  </div>
                </div>

                <v-divider
                  class="my-2"
                  v-if="
                    selectedInvoice.updated_at &&
                    selectedInvoice.updated_at !== selectedInvoice.added_at
                  "
                ></v-divider>

                <div
                  class="py-1"
                  v-if="
                    selectedInvoice.updated_at &&
                    selectedInvoice.updated_at !== selectedInvoice.added_at
                  "
                >
                  <div
                    class="font-weight-600 font-size-12 grey--text text--darken-2 mb-1"
                  >
                    Last Updated By:
                  </div>
                  <TableActivity
                    :data="selectedInvoice"
                    v-if="!lodash.isEmpty(selectedInvoice.updated_by)"
                  >
                    <template v-slot:first_letters>
                      {{
                        getFirstLetters(
                          selectedInvoice.updated_by?.display_name
                        )
                      }}
                    </template>
                    <template v-slot:display_name>
                      {{ selectedInvoice.updated_by.display_name }}
                    </template>
                    <template v-slot:date_time>
                      <template
                        v-if="getDateCountAll(selectedInvoice.updated_at)"
                        >{{ selectedInvoice.modified_at }}</template
                      >
                      <template v-else>{{
                        formatDateTime(selectedInvoice.updated_at)
                      }}</template>
                    </template>
                    <template v-slot:format_date_time>
                      {{ formatDateTime(selectedInvoice.updated_at) }}
                    </template>
                  </TableActivity>
                  <div v-else class="font-size-12 grey--text">
                    {{ formatDateTime(selectedInvoice.updated_at) }}
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Remarks Section -->
          <v-card flat class="custom-grey-border rounded-lg pa-3 mb-2">
            <div
              class="caption text-uppercase font-weight-700 grey--text text--darken-2 mb-2 d-flex align-center"
            >
              <v-icon small color="cyan darken-2" class="mr-1"
                >mdi-comment-text-outline</v-icon
              >
              Remarks & Notes
            </div>
            <div
              v-if="selectedInvoice.remark && selectedInvoice.remark.trim()"
              class="pa-3 grey lighten-5 rounded font-size-13"
              style="
                border-left: 3px solid #00bcd4;
                line-height: 1.6;
                word-break: break-word;
              "
            >
              {{ selectedInvoice.remark }}
            </div>
            <div v-else class="grey--text font-italic font-size-13 pa-2">
              No remark entered for this invoice.
            </div>
          </v-card>
        </v-container>
      </template>

      <template v-slot:action>
        <v-btn
          depressed
          class="custom-grey-border custom-bold-button"
          @click="viewDialog = false"
        >
          Close
        </v-btn>
        <v-btn
          v-if="
            isApproved && selectedInvoice && selectedInvoice.status !== 'Paid'
          "
          depressed
          color="cyan"
          class="custom-bold-button white--text ml-2"
          @click="editFromDetail"
        >
          <v-icon small left>mdi-pencil</v-icon>
          Edit Invoice
        </v-btn>
      </template>
    </Dialog>
  </div>
</template>

<script>
import CommonMixin from "@/core/plugins/common-mixin";
import Barcode from "@/view/pages/partials/Barcode.vue";
import ListingTable from "@/view/pages/partials/Listing-Table.vue";
import ListingFooter from "@/view/pages/partials/Listing-Footer.vue";
import TableActivity from "@/view/pages/partials/Table-Activity.vue";
import TextAreaField from "@/view/components/app-component/TextAreaField.vue";
import DateRangePicker from "@/view/components/DateRangePicker";
import DatePicker from "@/view/pages/partials/Datepicker.vue";
import Dialog from "@/view/pages/partials/Dialog.vue";
import { PaginationEventBus } from "@/core/lib/pagination.lib";
import { QUERY, POST } from "@/core/services/store/request.module";
import {
  /* SuccessEventBus, */
  ErrorEventBus,
  InitializeError,
} from "@/core/lib/message.lib";
import ValidationMixin from "@/core/plugins/validation-mixin";

export default {
  name: "POInvoice",
  mixins: [CommonMixin, ValidationMixin],
  components: {
    Barcode,
    ListingTable,
    ListingFooter,
    TableActivity,
    TextAreaField,
    DateRangePicker,
    DatePicker,
    Dialog,
  },
  props: {
    purchaseOrder: {
      type: [Number, String],
      required: true,
    },
    detail: {
      type: Object,
      default: () => ({}),
    },
    isApproved: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      invoiceLoading: false,
      formLoading: false,
      invoiceDialog: false,
      viewDialog: false,
      dateMenu: false,
      isEdit: false,
      dates: [],
      search: null,
      currentPage: 1,
      perPage: 10,
      totalPages: 0,
      totalRows: 0,
      rowsOffset: 0,
      rowData: [],
      selectedInvoice: null,
      formValid: true,
      poPaymentStatus: {
        status: "unpaid",
        status_text: "Unpaid",
      },
      form: {
        id: null,
        invoice_title: "",
        invoice_date: new Date().toISOString().substr(0, 10),
        invoice_number: "",
        total_amount: 0,
        paid_amount: 0,
        remark: "",
      },
    };
  },
  computed: {
    showingFrom() {
      return this.lodash.isEmpty(this.rowData) === false
        ? this.rowsOffset + 1
        : 0;
    },
    showingTo() {
      return this.rowsOffset + (this.rowData ? this.rowData.length : 0);
    },
    totalInvoicedAmount() {
      return (this.rowData || []).reduce(
        (sum, item) => sum + (parseFloat(item.total_amount) || 0),
        0
      );
    },
    totalPaidAmount() {
      return (this.rowData || []).reduce(
        (sum, item) => sum + (parseFloat(item.paid_amount) || 0),
        0
      );
    },
    totalBalanceAmount() {
      return (this.rowData || []).reduce(
        (sum, item) => sum + (parseFloat(item.balance_amount) || 0),
        0
      );
    },
    summaryMetrics() {
      const hasServerMetrics =
        this.poPaymentStatus &&
        this.poPaymentStatus.total_invoices !== undefined &&
        this.poPaymentStatus.total_invoices !== null;

      if (hasServerMetrics) {
        return {
          totalCount: Number(this.poPaymentStatus.total_invoices) || 0,
          totalAmount:
            parseFloat(this.poPaymentStatus.total_invoice_amount) || 0,
          paidCount: Number(this.poPaymentStatus.paid_count) || 0,
          paidAmount: parseFloat(this.poPaymentStatus.paid_amount) || 0,
          partiallyPaidCount:
            Number(this.poPaymentStatus.partially_paid_count) || 0,
          partiallyPaidAmount:
            parseFloat(this.poPaymentStatus.partially_paid_amount) || 0,
          partiallyPaidBalance:
            parseFloat(this.poPaymentStatus.partially_paid_balance) || 0,
          unpaidCount: Number(this.poPaymentStatus.unpaid_count) || 0,
          unpaidAmount: parseFloat(this.poPaymentStatus.unpaid_amount) || 0,
          balanceAmount:
            parseFloat(this.poPaymentStatus.total_balance_amount) || 0,
        };
      }

      const rows = this.rowData || [];
      const paidRows = rows.filter((r) => r.status === "Paid");
      const partialRows = rows.filter((r) => r.status === "Partially Paid");
      const unpaidRows = rows.filter((r) => r.status === "Unpaid");

      const totalAmount = rows.reduce(
        (sum, item) => sum + (parseFloat(item.total_amount) || 0),
        0
      );
      const paidAmount = paidRows.reduce(
        (sum, item) => sum + (parseFloat(item.paid_amount) || 0),
        0
      );
      const partiallyPaidAmount = partialRows.reduce(
        (sum, item) => sum + (parseFloat(item.paid_amount) || 0),
        0
      );
      const partiallyPaidBalance = partialRows.reduce(
        (sum, item) => sum + (parseFloat(item.balance_amount) || 0),
        0
      );
      const unpaidAmount = unpaidRows.reduce(
        (sum, item) => sum + (parseFloat(item.total_amount) || 0),
        0
      );
      const balanceAmount = rows.reduce(
        (sum, item) => sum + (parseFloat(item.balance_amount) || 0),
        0
      );

      return {
        totalCount: this.totalRows || rows.length,
        totalAmount,
        paidCount: paidRows.length,
        paidAmount,
        partiallyPaidCount: partialRows.length,
        partiallyPaidAmount,
        partiallyPaidBalance,
        unpaidCount: unpaidRows.length,
        unpaidAmount,
        balanceAmount,
      };
    },
    totalAmountRules() {
      return [
        (v) =>
          (v !== null && v !== "" && v !== undefined) ||
          "Total Amount is required",
        (v) => !isNaN(v) || "Total Amount must be a valid number",
        (v) => Number(v) >= 0 || "Total Amount cannot be negative",
      ];
    },
    paidAmountRules() {
      return [
        (v) =>
          (v !== null && v !== "" && v !== undefined) ||
          "Paid Amount is required",
        (v) => !isNaN(v) || "Paid Amount must be a valid number",
        (v) => Number(v) >= 0 || "Paid Amount cannot be negative",
        (v) =>
          Number(v) <= Number(this.form.total_amount) ||
          "Paid Amount cannot exceed Total Amount",
      ];
    },
  },
  methods: {
    formatMoney(val) {
      if (
        this.$accountingJS &&
        typeof this.$accountingJS.formatMoney === "function"
      ) {
        return this.$accountingJS.formatMoney(val);
      }
      return "$" + parseFloat(val || 0).toFixed(2);
    },
    onFilterChange() {
      this.currentPage = 1;
      this.getInvoices();
    },
    clearFilter(field) {
      const _this = this;
      switch (field) {
        case "search":
          _this.search = null;
          break;
        case "dates":
          _this.dates = [];
          break;
      }
      _this.currentPage = 1;
      _this.getInvoices();
    },
    handlePagination(page) {
      this.currentPage = page;
      this.getInvoices();
    },
    getInvoices() {
      if (!this.purchaseOrder || Number(this.purchaseOrder) <= 0) return;

      const data = {
        current_page: this.currentPage,
        per_page: this.perPage,
        search: this.search,
        dates: this.dates,
      };

      if (this.invoiceLoading) {
        return false;
      }

      this.invoiceLoading = true;

      this.$store
        .dispatch(QUERY, {
          url: `purchase-order/${this.purchaseOrder}/invoices`,
          data: data,
        })
        .then(({ data: resData }) => {
          const payload =
            resData && resData.rows !== undefined
              ? resData
              : resData && resData.data
              ? resData.data
              : {};

          this.rowData = Array.isArray(payload.rows) ? payload.rows : [];
          this.totalRows = payload.total_rows || 0;
          this.perPage = payload.per_page || 10;
          this.totalPages = Math.ceil(this.totalRows / this.perPage);
          this.currentPage = payload.current_page || 1;
          this.rowsOffset = payload.offset || 0;

          if (payload.po_payment_status) {
            this.poPaymentStatus = payload.po_payment_status;
          }
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.invoiceLoading = false;
        });
    },
    openCreateDialog() {
      /* console.log(this.$refs.poInvoiceForm); */
      this.$refs?.poInvoiceForm?.validate();

      if (!this.isApproved) return;
      this.isEdit = false;
      const defaultTotal =
        this.detail && this.detail.total ? parseFloat(this.detail.total) : 0;
      this.form = {
        id: null,
        invoice_title: null,
        invoice_date: new Date().toISOString().substr(0, 10),
        invoice_number: null,
        total_amount: defaultTotal,
        paid_amount: 0,
        remark: null,
      };
      this.invoiceDialog = true;
      this.$nextTick(() => {
        if (this.$refs.poInvoiceForm) {
          this.$refs.poInvoiceForm.resetValidation();
        }
      });
    },
    openEditDialog(row) {
      if (!this.isApproved) return;
      if (row.status === "Paid") {
        ErrorEventBus.$emit("update:error", [
          "Paid invoices cannot be edited.",
        ]);
        return;
      }
      this.isEdit = true;
      this.form = {
        id: row.id,
        invoice_title: row.invoice_title,
        invoice_date: row.invoice_date,
        invoice_number: row.invoice_number,
        total_amount: parseFloat(row.total_amount) || 0,
        paid_amount: parseFloat(row.paid_amount) || 0,
        remark: row.remark || "",
      };
      this.invoiceDialog = true;
      this.$nextTick(() => {
        if (this.$refs.poInvoiceForm) {
          this.$refs.poInvoiceForm.resetValidation();
        }
      });
    },
    closeInvoiceDialog() {
      this.invoiceDialog = false;
    },
    submitInvoice() {
      if (!this.$refs.poInvoiceForm || !this.$refs.poInvoiceForm.validate()) {
        return;
      }

      const paid = parseFloat(this.form.paid_amount);
      const total = parseFloat(this.form.total_amount);

      if (paid < 0 || total < 0) {
        ErrorEventBus.$emit("update:error", ["Amounts cannot be negative."]);
        return;
      }

      if (paid > total) {
        ErrorEventBus.$emit("update:error", [
          "Paid Amount cannot exceed Total Amount.",
        ]);
        return;
      }

      const _this = this;
      _this.formLoading = true;

      // Maintain single unified API for both create and update
      const payload = {
        id: _this.form.id || null,
        invoice_title: _this.form.invoice_title,
        invoice_date: _this.form.invoice_date,
        invoice_number: _this.form.invoice_number,
        paid_amount: paid,
        total_amount: total,
        remark: _this.form.remark || null,
      };

      const url = `purchase-order/${_this.purchaseOrder}/invoices`;

      _this.$store
        .dispatch(POST, { url, data: payload })
        .then(({ data: resData }) => {
          _this.closeInvoiceDialog();
          _this.getInvoices();
          if (_this.isEdit) {
            _this.$emit("invoice:updated", resData);
          } else {
            _this.$emit("invoice:created", resData);
          }
        })
        .catch((error) => {
          _this.logError(error);
          ErrorEventBus.$emit("update:error", InitializeError(error));
        })
        .finally(() => {
          _this.formLoading = false;
        });
    },
    viewInvoice(item) {
      this.selectedInvoice = item;
      this.viewDialog = true;
    },
    editFromDetail() {
      const item = this.selectedInvoice;
      this.viewDialog = false;
      this.$nextTick(() => {
        if (item) {
          this.openEditDialog(item);
        }
      });
    },
    truncateText(text, length = 42) {
      if (!text) return "";
      if (text.length <= length) return text;
      return text.substring(0, length) + "...";
    },
    calculatePaymentPercentage(invoice) {
      if (!invoice) return 0;
      const total = parseFloat(invoice.total_amount) || 0;
      const paid = parseFloat(invoice.paid_amount) || 0;
      if (total <= 0) return 0;
      const pct = Math.round((paid / total) * 100);
      return Math.min(100, Math.max(0, pct));
    },
    getInvoiceStatusTextColor(status) {
      if (status === "Paid") return "green--text text--darken-2";
      if (status === "Partially Paid") return "orange--text text--darken-2";
      if (status === "Unpaid") return "red--text text--darken-2";
      return "grey--text text--darken-2";
    },
    getInvoiceStatusColor(status) {
      if (status === "Paid") {
        return "green lighten-1";
      }
      if (status === "Partially Paid") {
        return "orange darken-1";
      }
      if (status === "Unpaid") {
        return "red lighten-1";
      }
      return "grey";
    },
  },
  mounted() {
    this.getInvoices();
    PaginationEventBus.$on("update:pagination", this.handlePagination);
  },
  beforeDestroy() {
    PaginationEventBus.$off("update:pagination", this.handlePagination);
  },
  watch: {
    purchaseOrder: {
      immediate: true,
      handler(val) {
        if (val && Number(val) > 0) {
          this.currentPage = 1;
          this.getInvoices();
        }
      },
    },
    detail: {
      deep: true,
      handler(val) {
        if (val && val.payment_status_summary) {
          this.poPaymentStatus = val.payment_status_summary;
        }
      },
    },
  },
};
</script>

<style scoped>
.po-invoice-listing {
  width: 100%;
}

.po-invoice-listing
  ::v-deep
  .custom-listing-table
  .simple-table.custom-table-height
  .v-data-table__wrapper {
  height: calc(100vh - 370px);
  /* max-height: calc(100vh - 360px); */
  min-height: 220px;
  overflow-y: auto;
}

.po-invoice-listing ::v-deep .simple-table table thead {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: #f3f6f9;
}
</style>
